import React, { useState } from 'react';
import '../styles/ClarificationCard.css';
import FaqModal from './FaqModal';

/**
 * ClarificationCard — shown when an agent needs more information.
 *
 * Driven entirely by the `questions` payload — no agent-specific logic here.
 *
 * Supported question types (set via q.type):
 *   "yes_no"     — Yes / No single-select; submitted as boolean
 *   "select"     — Single-select from q.options list
 *   "multiselect"— Multi-select from q.options list; submitted as array
 *   "text"       — Free-form text input (default fallback)
 *
 * On submit, `onSubmit` receives a JSON string:
 *   { [q.id]: value, ... }
 * where value is boolean (yes_no), string (select/text), or array (multiselect).
 *
 * Props:
 *   questions  — Array of question objects with id, question, type, options?, required?
 *   onSubmit   — Callback(answersJson: string)
 *   agentName  — Display name of the agent asking
 *   agentIcon  — Emoji icon for the agent
 */
export default function ClarificationCard({
  questions = [],
  onSubmit,
  agentName = 'Assistant',
  agentIcon = '🤖',
}) {
  const [selectedOptions, setSelectedOptions] = useState({});
  const [freeformAnswers, setFreeformAnswers] = useState({});
  const [showFaq, setShowFaq] = useState(false);

  const isMultiselect  = (q) => q.type === 'multiselect';
  const isYesNo        = (q) => q.type === 'yes_no';
  const isSelect       = (q) => q.type === 'select' || (!q.type && q.options?.length > 0);
  const isFreeform     = (q) => q.type === 'text' || (!q.type && !q.options?.length);
  const isAcknowledge  = (q) => q.type === 'acknowledge';

  // True when the card is purely informational (all questions are acknowledge type)
  const allAcknowledge = questions.length > 0 && questions.every(isAcknowledge);

  // Indices of yes_no questions (used for "Select All" logic)
  const yesNoIndices = questions.reduce((acc, q, i) => {
    if (isYesNo(q)) acc.push(i);
    return acc;
  }, []);
  const hasYesNoQuestions = yesNoIndices.length > 0;

  // Whether every yes_no question is currently answered 'yes'
  const allYesNoSelected =
    hasYesNoQuestions && yesNoIndices.every((i) => selectedOptions[i] === 'yes');

  const handleSelectAll = () => {
    if (allYesNoSelected) {
      // Deselect all yes_no
      setSelectedOptions((prev) => {
        const next = { ...prev };
        yesNoIndices.forEach((i) => { next[i] = undefined; });
        return next;
      });
    } else {
      // Select 'yes' for every yes_no question
      setSelectedOptions((prev) => {
        const next = { ...prev };
        yesNoIndices.forEach((i) => { next[i] = 'yes'; });
        return next;
      });
    }
  };

  const handleOptionToggle = (qIdx, value, multi) => {
    if (multi) {
      setSelectedOptions((prev) => {
        const current = Array.isArray(prev[qIdx]) ? prev[qIdx] : [];
        const exists = current.includes(value);
        return {
          ...prev,
          [qIdx]: exists ? current.filter((v) => v !== value) : [...current, value],
        };
      });
    } else {
      setSelectedOptions((prev) => ({ ...prev, [qIdx]: value }));
      setFreeformAnswers((prev) => ({ ...prev, [qIdx]: '' }));
    }
  };

  const handleFreeformChange = (qIdx, value) => {
    setFreeformAnswers((prev) => ({ ...prev, [qIdx]: value }));
    if (value) setSelectedOptions((prev) => ({ ...prev, [qIdx]: undefined }));
  };

  const isAnswered = (q, i) => {
    if (!q.required) return true;
    if (isAcknowledge(q)) return true;
    if (isMultiselect(q)) return Array.isArray(selectedOptions[i]) && selectedOptions[i].length > 0;
    if (isYesNo(q) || isSelect(q)) return selectedOptions[i] !== undefined && selectedOptions[i] !== null;
    return !!(freeformAnswers[i]?.trim() || selectedOptions[i] !== undefined);
  };

  const isSubmittable = allAcknowledge || questions.length === 0 || questions.every((q, i) => isAnswered(q, i));

  const handleSubmit = () => {
    if (!isSubmittable) return;

    const answers = {};
    questions.forEach((q, i) => {
      const sel = selectedOptions[i];
      const free = freeformAnswers[i]?.trim();

      if (isAcknowledge(q)) {
        answers[q.id] = true;
      } else if (isYesNo(q)) {
        answers[q.id] = sel === 'yes';
      } else if (isMultiselect(q)) {
        answers[q.id] = Array.isArray(sel) ? sel : [];
      } else if (sel !== undefined && sel !== null) {
        answers[q.id] = sel;
      } else if (free) {
        answers[q.id] = free;
      }
    });

    onSubmit(JSON.stringify(answers));
  };

  const YES_NO_OPTIONS = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ];

  const getOptions = (q) => {
    if (isYesNo(q)) return YES_NO_OPTIONS;
    if (!q.options) return [];
    return q.options.map((o) =>
      typeof o === 'object' ? o : { label: String(o), value: o, disabled: false }
    );
  };

  return (
    <>
      {showFaq && <FaqModal onClose={() => setShowFaq(false)} />}

      <div className="clarification-card">
        <div className="clarification-header">
          <span className="clarification-badge">{agentIcon} {agentName}</span>
          <p className="clarification-subtitle">
            {allAcknowledge
              ? 'Here is the information you requested:'
              : 'A few quick questions to give you the best answer:'}
          </p>
        </div>

        <div className="clarification-body">
          {questions.map((q, qIdx) => {
            const multi = isMultiselect(q);
            const opts = getOptions(q);
            const freeform = isFreeform(q);

            // ── Acknowledge (info-only) ───────────────────────────────────
            if (isAcknowledge(q)) {
              return (
                <div key={q.id || qIdx} className="clarification-acknowledge">
                  {q.header && (
                    <div className="clarification-acknowledge-header">{q.header}</div>
                  )}
                  <div className="clarification-acknowledge-body">{q.question}</div>
                </div>
              );
            }

            return (
              <div key={q.id || qIdx} className="clarification-question">
                <div className="clarification-question-text">
                  {q.required && <span className="clarification-required">*</span>}
                  {q.question}
                </div>

                {opts.length > 0 && (
                  <div className={`clarification-options${multi ? ' clarification-options--multi' : ''}`}>
                    {opts.map((opt, oIdx) => {
                      const isActive = multi
                        ? Array.isArray(selectedOptions[qIdx]) && selectedOptions[qIdx].includes(opt.value)
                        : selectedOptions[qIdx] === opt.value;
                      const isDisabled = !!opt.disabled;

                      return (
                        <button
                          key={oIdx}
                          className={`clarification-option${isActive ? ' selected' : ''}${isDisabled ? ' disabled' : ''}${isYesNo(q) ? ` clarification-option--${opt.value}` : ''}`}
                          onClick={() => !isDisabled && handleOptionToggle(qIdx, opt.value, multi)}
                          disabled={isDisabled}
                          title={isDisabled ? 'Already claimed' : undefined}
                        >
                          {multi && (
                            <span className="clarification-checkbox">
                              {isDisabled ? '✓' : isActive ? '✓' : ''}
                            </span>
                          )}
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {freeform && (
                  <input
                    type="text"
                    className="clarification-freeform"
                    placeholder="Type your answer..."
                    value={freeformAnswers[qIdx] || ''}
                    onChange={(e) => handleFreeformChange(qIdx, e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && isSubmittable) handleSubmit(); }}
                  />
                )}
              </div>
            );
          })}

          {/* ── Select All / certification row (only when yes_no questions exist) ── */}
          {hasYesNoQuestions && (
            <div className="clarification-select-all">
              <button
                className={`clarification-select-all-btn${allYesNoSelected ? ' active' : ''}`}
                onClick={handleSelectAll}
                type="button"
              >
                <span className="clarification-select-all-check">
                  {allYesNoSelected ? '✓' : ''}
                </span>
                <span className="clarification-select-all-text">
                  I certify that the information given above is true and to the best of my
                  knowledge. Anything found on the contrary, I&apos;ll be liable for necessary
                  actions by the Company.{' '}
                  <span className="clarification-note">
                    Note: Refer{' '}
                    <button
                      className="clarification-faq-link"
                      onClick={(e) => { e.stopPropagation(); setShowFaq(true); }}
                      type="button"
                    >
                      FAQ
                    </button>{' '}
                    to check eligibility criteria.
                  </span>
                </span>
              </button>
            </div>
          )}
        </div>

        <div className="clarification-footer">
          <button
            className="clarification-submit"
            onClick={handleSubmit}
            disabled={!isSubmittable}
          >
            {allAcknowledge ? 'OK' : 'Submit & Continue →'}
          </button>
        </div>
      </div>
    </>
  );
}
